import {FC, MouseEventHandler, useEffect, useState} from "react";
import {Breadcrumb, Button, Col, Container, Row, Table} from "react-bootstrap";
import {Footer, FormText, useLoader, useMessage} from "backoffice/ui/Components";
import {useNavigate, useParams} from "react-router";
import {useTRPC} from "backoffice/ui/App";
import {Helmet} from "react-helmet";
import {WithdrawApplication} from "src/rds/types/crowdbank/t_withdraw";
import {WithdrawTask} from "src/rds/types/crowdbank/t_withdraw_task.ts";


export const MembersWithdrawDetailPage: FC = () => {

  const {id} = useParams();

  const Loader   = useLoader(),
        Message  = useMessage(),
        Navigate = useNavigate(),
        tRPC     = useTRPC();

  const [task, setTask]       = useState<WithdrawTask | null>(null),
        [list, setList]       = useState<WithdrawApplication[]>([]),
        [keyword, setKeyword] = useState(''),
        keywords              = keyword.trim().replace(/\s+/g, ' ').split(' ').filter(el => el),
        [ts, setTs]           = useState('');


  // キーワードによるフィルターされた出金申請
  const filtered = list.filter(elem => {
    for (const keyword of keywords) {

      if (elem.user_id !== keyword && elem.proc_account_number !== keyword) {
        return false;
      }
    }
    return true;
  });


  // 初期化
  useEffect(() => {
    Loader.task(() => tRPC.member.withdraw.detail.query({id: Number(id)}), 300)
          .then(({task, list}) => {
            setTask(task);
            setList(list);
          })
          .catch(err => {
            console.error(err);
            Message.error(err);
          });
  }, [id, ts]);

  const handleClick: MouseEventHandler<HTMLElement> = event => {
    setKeyword(`${keyword} ${event.currentTarget?.innerText}`.trim())
  }

  const handleCancel = () => {
    if (!task) {
      return;
    }

    const targetIds = filtered.map(elem => elem.record_number);
    if (targetIds.length === 0) {
      return;
    }

    const [cancelAPI, revert] = (() => {
      if (task.next_action === '全銀協ファイル登録') {
        return [
          tRPC.member.withdraw.cancelAtRegister,
          task.data.cancels.at_register.includes(targetIds[0])
        ];

      } else if (task.next_action === '出金確認') {
        return [
          tRPC.member.withdraw.cancelAfterRegister,
          task.data.cancels.after_register.includes(targetIds[0])
        ];
      }
      throw new Error('キャンセル出来るステータスではありません');
    })();

    const request = {
      id        : task.id,
      updated_at: task.updated_at,
      targetIds, revert
    };


    Loader.task(() => cancelAPI.mutate(request), 300)
          .then(() => {
            Message.show(`${revert ? 'キャンセル解除登録' : 'キャンセル登録'}が完了しました`, () => {
              setKeyword('');
              setTs(String(Date.now()));
            });
          })
          .catch(err => {
            console.error(err);
            Message.error(err);
          });
  }

  // キャンセル/revert 可能
  const cancel = {
    targets: filtered.filter(el => !el.cancel_flg),
    enable : task?.next_action === '全銀協ファイル登録' || task?.next_action === "出金確認"
  };

  return (
      <>
        <Helmet>
          <title>出金管理 #{id}</title>
        </Helmet>

        <div className={'container'}>
          <Breadcrumb>
            <Breadcrumb.Item active>資金管理</Breadcrumb.Item>
            <Breadcrumb.Item onClick={() => Navigate('../')}>出金管理</Breadcrumb.Item>
            <Breadcrumb.Item active>#{id}</Breadcrumb.Item>
          </Breadcrumb>
        </div>

        <Container>

          <Row>
            <Col className={'p-5'}>
              <FormText value={keyword}
                        onChange={event => setKeyword(event.currentTarget.value)}
                        placeholder={'「ユーザーID」もしくは「口座番号」、スペース区切りでAND条件'}/>
            </Col>
          </Row>

          <Table>
            <thead>
            <tr>
              <th className={'text-end   '}>ID</th>
              <th className={'text-center'}>ユーザーID</th>
              <th className={'text-end   '}>金額</th>
              <th className={'text-start '}>銀行</th>
              <th className={'text-start '}>支店</th>
              <th className={'text-center'}>区分</th>
              <th className={'text-center'}>口座番号</th>
              <th className={'text-start '}>名義</th>
            </tr>
            </thead>
            <tbody>
            {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className={'text-center'}>なし</td>
                </tr>
            )}
            {filtered.map(elem => {
              const canceled = elem.cancel_flg
                               || task!.data.cancels.at_register.includes(elem.record_number)
                               || task!.data.cancels.after_register.includes(elem.record_number),
                    style    = canceled ? {background: 'gray'} : undefined;

              return (
                  <tr key={elem.record_number}>
                    <td style={style} className={'align-middle text-end   '}>{elem.record_number}</td>
                    <td style={style} className={'align-middle text-center cursor-pointer'} onClick={handleClick}>
                      {elem.user_id}
                    </td>
                    <td style={style} className={'align-middle text-end   '}>{elem.amount.toString().withComma}</td>
                    <td style={style} className={'align-middle text-start '}>{elem.proc_bank}</td>
                    <td style={style} className={'align-middle text-start '}>{elem.proc_branch}</td>
                    <td style={style} className={'align-middle text-center'}>{elem.proc_account_type_id}</td>
                    <td style={style} className={'align-middle text-center cursor-pointer'} onClick={handleClick}>
                      {elem.proc_account_number}
                    </td>
                    <td style={style} className={'align-middle text-start '}>{elem.proc_account_name}</td>
                  </tr>
              );
            })}
            </tbody>
          </Table>
        </Container>

        {cancel.enable && (
            <Footer>
              <Button variant="outline-danger" className={'m-2'} style={{minWidth: 100}} size={'sm'}
                      onClick={handleCancel}
                      disabled={cancel.targets.length === 0}>
                キャンセル 登録 / 解除（{cancel.targets.length}件）
              </Button>
            </Footer>
        )}
      </>
  );
}
